import { PDFDocument, PDFFont, PDFPage, rgb, RGB, StandardFonts } from 'pdf-lib';
import {
  FontStyle,
  LayoutOp,
  layoutInspectionReport,
  RectOp,
  StrokeColour,
  TextColour,
  TextMeasurer,
  TextOp,
} from './inspection-report.layout';
import { InspectionReportViewModel } from './inspection-report.view-model';
import { ReportTheme, RgbColour, STATUS_ICON_BOX, STATUS_ICON_PATHS } from './report-theme';
import { toWinAnsiText } from './win-ansi-text';

type EmbeddedFonts = Record<FontStyle, PDFFont>;

const PDF_MIME_TYPE = 'application/pdf';

export async function renderInspectionReportPdf(viewModel: InspectionReportViewModel): Promise<Blob> {
  const bytes = await renderInspectionReportPdfBytes(viewModel);
  return new Blob([bytes], { type: PDF_MIME_TYPE });
}

export async function renderInspectionReportPdfBytes(viewModel: InspectionReportViewModel): Promise<Uint8Array> {
  const document = await PDFDocument.create();
  document.setTitle(toWinAnsiText(viewModel.header.title));

  const fonts: EmbeddedFonts = {
    [FontStyle.Regular]: await document.embedFont(StandardFonts.Helvetica),
    [FontStyle.Bold]: await document.embedFont(StandardFonts.HelveticaBold),
  };
  const measure: TextMeasurer = (text, size, style) => fonts[style].widthOfTextAtSize(toWinAnsiText(text), size);

  const layout = layoutInspectionReport(viewModel, measure);
  layout.pages.forEach((layoutPage) => {
    const page = document.addPage([layout.pageWidth, layout.pageHeight]);
    layoutPage.ops.forEach((op) => paintOp(page, op, fonts, layout.pageHeight));
  });

  return document.save();
}

function paintOp(page: PDFPage, op: LayoutOp, fonts: EmbeddedFonts, pageHeight: number): void {
  switch (op.kind) {
    case 'text':
      paintText(page, op, fonts, pageHeight);
      return;
    case 'icon':
      // drawSvgPath takes the glyph's top-left corner and draws downward, matching SVG space.
      page.drawSvgPath(STATUS_ICON_PATHS[op.status], {
        x: op.x,
        y: pageHeight - op.top,
        scale: op.size / STATUS_ICON_BOX,
        color: toPdfColour(ReportTheme.statusColours[op.status]),
      });
      return;
    case 'line':
      page.drawLine({
        start: { x: op.x1, y: pageHeight - op.y1 },
        end: { x: op.x2, y: pageHeight - op.y2 },
        thickness: op.thickness,
        color: toPdfColour(ReportTheme.colours.rule),
      });
      return;
    case 'rect':
      paintRect(page, op, pageHeight);
      return;
  }
}

// Standard fonts carry no underline, so an underlined run is a rule drawn just below its baseline.
function paintText(page: PDFPage, op: TextOp, fonts: EmbeddedFonts, pageHeight: number): void {
  const text = toWinAnsiText(op.text);
  const font = fonts[op.style];
  const color = toPdfColour(op.colour === TextColour.Muted ? ReportTheme.colours.muted : ReportTheme.colours.text);
  const y = pageHeight - op.baseline;

  page.drawText(text, { x: op.x, y, size: op.size, font, color });

  if (op.underline) {
    const underlineY = y - ReportTheme.underlineOffset;
    page.drawLine({
      start: { x: op.x, y: underlineY },
      end: { x: op.x + font.widthOfTextAtSize(text, op.size), y: underlineY },
      thickness: ReportTheme.underlineThickness,
      color,
    });
  }
}

// pdf-lib fills a rectangle black unless it is given a fill colour or only a border colour, so
// the fill is passed only when the op asks for one.
function paintRect(page: PDFPage, op: RectOp, pageHeight: number): void {
  const borderColor = toPdfColour(
    op.stroke === StrokeColour.Border ? ReportTheme.colours.border : ReportTheme.colours.rule
  );
  const fill = op.fill === undefined ? {} : { color: toPdfColour(ReportTheme.colours.band) };

  page.drawRectangle({
    x: op.x,
    y: pageHeight - op.top - op.height,
    width: op.width,
    height: op.height,
    borderWidth: op.thickness,
    borderColor,
    ...fill,
  });
}

function toPdfColour(colour: RgbColour): RGB {
  return rgb(colour.r, colour.g, colour.b);
}
